import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import {
  Plus as PlusIcon,
  X as XMarkIcon,
  Users as UserGroupIcon,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  FileText as DocumentTextIcon,
  Check as CheckIcon,
  ArrowRight as ArrowRightIcon
} from 'lucide-react';

const MeetingEditor = () => {
  const navigate = useNavigate();
  const { meetingId } = useParams();
  const { currentUser, hasPermission } = useAuth();
  const {
    meetings,
    users,
    departments,
    tasks,
    projects,
    createMeeting,
    updateMeeting,
    completeMeeting,
    createNotification
  } = useData();

  // Get follow-up meeting ID from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const followUpMeetingId = searchParams.get('followUp');

  // Check if this is a new meeting
  const isNewMeeting = !meetingId || meetingId === 'new' || meetingId === 'undefined';
  const isFollowUpMeeting = !!followUpMeetingId;
  const canEditMeeting = hasPermission('deputy_ceo');
  
  console.log('MeetingEditor - Raw meetingId:', meetingId, 'Type:', typeof meetingId, 'isNewMeeting:', isNewMeeting, 'isFollowUp:', isFollowUpMeeting);

  // Meeting data state
  const [meetingData, setMeetingData] = useState({
    subject: '',
    purpose: '',
    mainDepartmentId: '',
    participantIds: [],
    startDate: new Date().toISOString().slice(0, 16),
    endDate: '',
    agendaItems: [],
    decisions: [],
    summary: '',
    newTasks: [],
    newProjects: [],
    linkedTaskIds: [],
    linkedProjectIds: [],
    attachments: [],
    status: 'scheduled', // ברירת מחדל לישיבה מתוכננת
    location: ''
  });

  const [currentAgendaItem, setCurrentAgendaItem] = useState('');
  const [currentDecision, setCurrentDecision] = useState('');
  const [saving, setSaving] = useState(false);
  const [previousMeeting, setPreviousMeeting] = useState(null);
  const [previousMeetingTasks, setPreviousMeetingTasks] = useState([]);
  const [previousMeetingProjects, setPreviousMeetingProjects] = useState([]);

  // Load existing meeting if editing
  useEffect(() => {
    console.log('useEffect - isNewMeeting:', isNewMeeting, 'meetingId:', meetingId);
    
    if (!isNewMeeting && meetingId && meetingId !== 'new') {
      console.log('Looking for meeting in meetings array:', meetings.length, 'meetings');
      const meeting = meetings.find(m => m.id === meetingId);
      
      if (meeting) {
        console.log('Found meeting:', meeting);
        setMeetingData({
          subject: meeting.subject || '',
          purpose: meeting.purpose || '',
          mainDepartmentId: meeting.mainDepartmentId || '',
          participantIds: meeting.participantIds || [],
          startDate: meeting.startDate?.toDate ? 
            meeting.startDate.toDate().toISOString().slice(0, 16) : 
            new Date().toISOString().slice(0, 16),
          endDate: meeting.endDate?.toDate ? 
            meeting.endDate.toDate().toISOString().slice(0, 16) : '',
          agendaItems: meeting.agendaItems || [],
          decisions: meeting.decisions || [],
          summary: meeting.summary || '',
          newTasks: meeting.newTasks || [],
          newProjects: meeting.newProjects || [],
          linkedTaskIds: meeting.linkedTaskIds || [],
          linkedProjectIds: meeting.linkedProjectIds || [],
          attachments: meeting.attachments || []
        });
      } else {
        console.log('Meeting not found with ID:', meetingId);
      }
    } else {
      console.log('Skipping load - this is a new meeting');
    }
  }, [meetingId, meetings, isNewMeeting]);

  // Load previous meeting data if this is a follow-up meeting
  useEffect(() => {
    if (isFollowUpMeeting && followUpMeetingId) {
      console.log('Loading previous meeting:', followUpMeetingId);
      const prevMeeting = meetings.find(m => m.id === followUpMeetingId);
      
      if (prevMeeting) {
        console.log('Found previous meeting:', prevMeeting);
        setPreviousMeeting(prevMeeting);
        
        // Copy participants and department from previous meeting
        setMeetingData(prev => ({
          ...prev,
          subject: `המשך - ${prevMeeting.subject}`,
          mainDepartmentId: prevMeeting.mainDepartmentId,
          participantIds: prevMeeting.participantIds,
          previousMeetingId: followUpMeetingId
        }));
        
        // Load tasks from previous meeting
        const prevTasks = [];
        if (prevMeeting.createdTaskIds && prevMeeting.createdTaskIds.length > 0) {
          prevMeeting.createdTaskIds.forEach(taskId => {
            const task = tasks.find(t => t.id === taskId);
            if (task) prevTasks.push(task);
          });
        }
        if (prevMeeting.linkedTaskIds && prevMeeting.linkedTaskIds.length > 0) {
          prevMeeting.linkedTaskIds.forEach(taskId => {
            const task = tasks.find(t => t.id === taskId);
            if (task && !prevTasks.find(t => t.id === taskId)) {
              prevTasks.push(task);
            }
          });
        }
        setPreviousMeetingTasks(prevTasks);
        
        // Load projects from previous meeting
        const prevProjects = [];
        if (prevMeeting.createdProjectIds && prevMeeting.createdProjectIds.length > 0) {
          prevMeeting.createdProjectIds.forEach(projectId => {
            const project = projects.find(p => p.id === projectId);
            if (project) prevProjects.push(project);
          });
        }
        if (prevMeeting.linkedProjectIds && prevMeeting.linkedProjectIds.length > 0) {
          prevMeeting.linkedProjectIds.forEach(projectId => {
            const project = projects.find(p => p.id === projectId);
            if (project && !prevProjects.find(p => p.id === projectId)) {
              prevProjects.push(project);
            }
          });
        }
        setPreviousMeetingProjects(prevProjects);
      }
    }
  }, [isFollowUpMeeting, followUpMeetingId, meetings, tasks, projects]);

  // Get users by department (for participant selection)
  const getUsersByDepartment = (deptId) => {
    return users.filter(user => {
      const userDepts = Array.isArray(user.departmentIds) ? user.departmentIds : 
                       user.departmentId ? [user.departmentId] : [];
      return userDepts.includes(deptId);
    });
  };

  // Get all active users for "other departments" section
  const getOtherDepartmentUsers = () => {
    return users.filter(user => {
      const userDepts = Array.isArray(user.departmentIds) ? user.departmentIds : 
                       user.departmentId ? [user.departmentId] : [];
      return !userDepts.includes(meetingData.mainDepartmentId);
    });
  };

  // Handle save draft
  const handleSaveDraft = async () => {
    if (!meetingData.subject || !meetingData.mainDepartmentId) {
      alert('נא למלא לפחות נושא ומחלקה ראשית');
      return;
    }

    setSaving(true);
    try {
      console.log('🔴 handleSaveDraft - Current User ID:', currentUser?.id);
      console.log('🔴 handleSaveDraft - Meeting Data:', meetingData);
      
      // וידוא שיוצר הישיבה כלול במשתתפים
      const participantIds = [...(meetingData.participantIds || [])];
      if (currentUser?.id && !participantIds.includes(currentUser.id)) {
        participantIds.push(currentUser.id);
        console.log('🟡 Added creator to participantIds:', currentUser.id);
      }

      // יוצר הישיבה אוטומטית מאושר (לא צריך לאשר את עצמו)
      const confirmedParticipantIds = [...(meetingData.confirmedParticipantIds || [])];
      if (currentUser?.id && !confirmedParticipantIds.includes(currentUser.id)) {
        confirmedParticipantIds.push(currentUser.id);
        console.log('🟢 Added creator to confirmedParticipantIds:', currentUser.id);
      }

      console.log('🟣 Final participantIds:', participantIds);
      console.log('🟣 Final confirmedParticipantIds:', confirmedParticipantIds);

      const dataToSave = {
        ...meetingData,
        participantIds,
        confirmedParticipantIds,
        startDate: new Date(meetingData.startDate),
        endDate: meetingData.endDate ? new Date(meetingData.endDate) : null
      };

      console.log('📦 Data to save:', dataToSave);

      if (isNewMeeting) {
        const newMeetingId = await createMeeting(dataToSave);

        // שליחת התראות למשתתפים אם זו ישיבה מתוכננת (לא בסטטוס completed)
        if (dataToSave.status === 'scheduled' && dataToSave.participantIds && dataToSave.participantIds.length > 0) {
          console.log('Sending notifications to participants...');

          for (const participantId of dataToSave.participantIds) {
            // דילוג על יוצר הישיבה - הוא לא צריך התראה על ישיבה שהוא יצר
            if (participantId === currentUser?.id) {
              console.log(`Skipping notification for meeting creator ${participantId}`);
              continue;
            }

            try {
              await createNotification({
                userId: participantId,
                type: 'meeting_scheduled',
                title: 'הוזמנת לישיבה',
                message: `הוזמנת לישיבה: "${dataToSave.subject}" ב-${new Date(dataToSave.startDate).toLocaleDateString('he-IL', {
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit'
                })}`,
                meetingId: newMeetingId,
                actionRequired: true
              });
              console.log(`Notification sent to participant ${participantId}`);
            } catch (notifError) {
              console.error(`Error sending notification to ${participantId}:`, notifError);
            }
          }
        }

        alert('הישיבה נשמרה בהצלחה והתראות נשלחו למשתתפים');
        navigate(`/meetings/${newMeetingId}`);
      } else {
        await updateMeeting(meetingId, dataToSave);
        alert('השינויים נשמרו בהצלחה');
      }
    } catch (error) {
      console.error('Error saving meeting:', error);
      alert('שגיאה בשמירת הישיבה');
    } finally {
      setSaving(false);
    }
  };

  // Handle complete meeting
  const handleCompleteMeeting = async () => {
    if (!meetingData.subject || !meetingData.summary) {
      alert('נא למלא נושא וסיכום לפני סיום הישיבה');
      return;
    }

    if (meetingData.newTasks.length === 0 && meetingData.newProjects.length === 0 &&
        meetingData.linkedTaskIds.length === 0 && meetingData.linkedProjectIds.length === 0) {
      if (!window.confirm('לא נוצרו משימות או פרויקטים. האם לסיים בכל זאת?')) {
        return;
      }
    }

    if (!window.confirm('האם לסיים את הישיבה? כל המשימות והפרויקטים יישלחו לאחראיים')) {
      return;
    }

    setSaving(true);
    try {
      // Filter out empty tasks and projects
      const validTasks = meetingData.newTasks.filter(task => 
        task.title && task.title.trim() !== ''
      );
      
      const validProjects = meetingData.newProjects.filter(project => 
        project.name && project.name.trim() !== ''
      );

      console.log('Valid tasks:', validTasks.length, 'Valid projects:', validProjects.length);

      const dataToSave = {
        ...meetingData,
        newTasks: validTasks,
        newProjects: validProjects,
        startDate: new Date(meetingData.startDate),
        endDate: new Date()
      };

      let currentMeetingId;
      
      console.log('Processing meeting - isNewMeeting:', isNewMeeting, 'meetingId:', meetingId);
      
      // If it's a new meeting, create it first
      if (isNewMeeting) {
        console.log('Creating new meeting...');
        currentMeetingId = await createMeeting(dataToSave);
        console.log('Meeting created with ID:', currentMeetingId);
        
        if (!currentMeetingId) {
          throw new Error('Failed to create meeting - no ID returned');
        }
      } else {
        console.log('Updating existing meeting:', meetingId);
        
        if (!meetingId) {
          throw new Error('No meeting ID provided for update');
        }
        
        currentMeetingId = meetingId;
        // Only update if the meeting already exists
        await updateMeeting(currentMeetingId, dataToSave);
      }

      // Verify we have a valid meeting ID
      if (!currentMeetingId || currentMeetingId === 'new') {
        throw new Error('Failed to get valid meeting ID: ' + currentMeetingId);
      }

      // Complete the meeting (creates tasks/projects and sends notifications)
      console.log('Completing meeting with ID:', currentMeetingId);
      const result = await completeMeeting(currentMeetingId, dataToSave);
      console.log('Meeting completed successfully:', result);
      
      alert('הישיבה הסתיימה בהצלחה! כל המשימות והפרויקטים נשלחו לאחראיים.');
      navigate('/meetings');
    } catch (error) {
      console.error('Error completing meeting:', error);
      console.error('Error stack:', error.stack);
      alert(`שגיאה בסיום הישיבה: ${error.message || 'שגיאה לא ידועה'}`);
    } finally {
      setSaving(false);
    }
  };

  // Add agenda item
  const addAgendaItem = () => {
    if (currentAgendaItem.trim()) {
      setMeetingData(prev => ({
        ...prev,
        agendaItems: [...prev.agendaItems, currentAgendaItem.trim()]
      }));
      setCurrentAgendaItem('');
    }
  };

  // Remove agenda item
  const removeAgendaItem = (index) => {
    setMeetingData(prev => ({
      ...prev,
      agendaItems: prev.agendaItems.filter((_, i) => i !== index)
    }));
  };

  // Add decision
  const addDecision = () => {
    if (currentDecision.trim()) {
      setMeetingData(prev => ({
        ...prev,
        decisions: [...prev.decisions, currentDecision.trim()]
      }));
      setCurrentDecision('');
    }
  };

  // Remove decision
  const removeDecision = (index) => {
    setMeetingData(prev => ({
      ...prev,
      decisions: prev.decisions.filter((_, i) => i !== index)
    }));
  };

  // Toggle participant
  const toggleParticipant = (userId) => {
    setMeetingData(prev => {
      const isSelected = prev.participantIds.includes(userId);
      return {
        ...prev,
        participantIds: isSelected
          ? prev.participantIds.filter(id => id !== userId)
          : [...prev.participantIds, userId]
      };
    });
  };

  // Add new task
  const addNewTask = () => {
    setMeetingData(prev => ({
      ...prev,
      newTasks: [
        ...prev.newTasks,
        {
          title: '',
          description: '',
          assigneeIds: [],
          deadline: '',
          priority: 'medium',
          scope: 'managerial'
        }
      ]
    }));
  };

  // Update new task
  const updateNewTask = (index, field, value) => {
    setMeetingData(prev => ({
      ...prev,
      newTasks: prev.newTasks.map((task, i) =>
        i === index ? { ...task, [field]: value } : task
      )
    }));
  };

  // Remove new task
  const removeNewTask = (index) => {
    setMeetingData(prev => ({
      ...prev,
      newTasks: prev.newTasks.filter((_, i) => i !== index)
    }));
  };

  // Add new project
  const addNewProject = () => {
    setMeetingData(prev => ({
      ...prev,
      newProjects: [
        ...prev.newProjects,
        {
          name: '',
          description: '',
          managerId: '',
          teamMembers: [],
          deadline: '',
          priority: 'medium'
        }
      ]
    }));
  };

  // Update new project
  const updateNewProject = (index, field, value) => {
    setMeetingData(prev => ({
      ...prev,
      newProjects: prev.newProjects.map((project, i) =>
        i === index ? { ...project, [field]: value } : project
      )
    }));
  };

  // Remove new project
  const removeNewProject = (index) => {
    setMeetingData(prev => ({
      ...prev,
      newProjects: prev.newProjects.filter((_, i) => i !== index)
    }));
  };

  // Toggle linked task
  const toggleLinkedTask = (taskId) => {
    setMeetingData(prev => {
      const isLinked = prev.linkedTaskIds.includes(taskId);
      return {
        ...prev,
        linkedTaskIds: isLinked
          ? prev.linkedTaskIds.filter(id => id !== taskId)
          : [...prev.linkedTaskIds, taskId]
      };
    });
  };

  // Toggle linked project
  const toggleLinkedProject = (projectId) => {
    setMeetingData(prev => {
      const isLinked = prev.linkedProjectIds.includes(projectId);
      return {
        ...prev,
        linkedProjectIds: isLinked
          ? prev.linkedProjectIds.filter(id => id !== projectId)
          : [...prev.linkedProjectIds, projectId]
      };
    });
  };

  if (!canEditMeeting) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">אין הרשאה</h2>
            <p className="text-gray-600 mb-6">
              רק מנכ"ל וסמנכ"ל יכולים ליצור ולערוך ישיבות
            </p>
            <button
              onClick={() => navigate('/meetings')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              חזרה לרשימת ישיבות
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/meetings')}
                className="p-3 hover:bg-gray-100 rounded-xl transition-all duration-200 hover:scale-110"
                title="חזרה לרשימת ישיבות"
              >
                <ArrowRightIcon className="w-6 h-6 text-gray-700" />
              </button>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  {isNewMeeting ? 'ישיבה חדשה' : 'עריכת ישיבה'}
                </h1>
                <p className="text-gray-600 mt-1">
                  {isNewMeeting ? 'צור ותעד את הישיבה במהלכה' : 'ערוך את פרטי הישיבה'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2"
              >
                <DocumentTextIcon className="w-5 h-5" />
                {saving ? 'שומר...' : 'שמור טיוטה'}
              </button>
              <button
                onClick={handleCompleteMeeting}
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold flex items-center gap-2"
              >
                <CheckIcon className="w-5 h-5" />
                {saving ? 'מסיים...' : 'סיים ישיבה'}
              </button>
            </div>
          </div>
        </div>

        {/* Previous Meeting Info - Show if this is a follow-up meeting */}
        {isFollowUpMeeting && previousMeeting && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl shadow-lg p-8 mb-6 border-2 border-green-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg">
                <ArrowRightIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-green-900">ישיבת המשך</h2>
                <p className="text-green-700">מקושרת לישיבה קודמת</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 mb-4 shadow-sm">
              <h3 className="font-bold text-lg text-gray-800 mb-3">פרטי הישיבה הקודמת:</h3>
              <div className="space-y-2">
                <p className="text-gray-700"><span className="font-semibold">נושא:</span> {previousMeeting.subject}</p>
                <p className="text-gray-700"><span className="font-semibold">תאריך:</span> {
                  previousMeeting.startDate?.toDate ? 
                    new Intl.DateTimeFormat('he-IL', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }).format(previousMeeting.startDate.toDate()) 
                    : 'לא זמין'
                }</p>
              </div>
            </div>

            {/* Previous Meeting Tasks */}
            {previousMeetingTasks.length > 0 && (
              <div className="bg-white rounded-xl p-6 mb-4 shadow-sm">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <CheckIcon className="w-5 h-5 text-green-600" />
                  משימות מהישיבה הקודמת ({previousMeetingTasks.length})
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {previousMeetingTasks.map(task => (
                    <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="text-gray-800">{task.title}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        task.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                        task.status === 'בתהליך' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {task.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Previous Meeting Projects */}
            {previousMeetingProjects.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                  <DocumentTextIcon className="w-5 h-5 text-green-600" />
                  פרויקטים מהישיבה הקודמת ({previousMeetingProjects.length})
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {previousMeetingProjects.map(project => (
                    <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="text-gray-800">{project.name}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        project.status === 'הושלם' ? 'bg-green-100 text-green-800' :
                        project.status === 'בתהליך' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 p-4 bg-green-100 rounded-lg border-r-4 border-green-500">
              <p className="text-green-900 text-sm">
                <strong>💡 טיפ:</strong> המשתתפים והמחלקה הועתקו אוטומטית מהישיבה הקודמת. אפשר לערוך אותם בהמשך הטופס.
              </p>
            </div>
          </div>
        )}

        {/* Basic Info */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl">
              <DocumentTextIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">פרטים בסיסיים</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                נושא הישיבה <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={meetingData.subject}
                onChange={(e) => setMeetingData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white text-lg"
                placeholder="למשל: דיון בתקציב רבעון ראשון"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                מטרת הישיבה <span className="text-red-500">*</span>
              </label>
              <textarea
                value={meetingData.purpose}
                onChange={(e) => setMeetingData(prev => ({ ...prev, purpose: e.target.value }))}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                rows="3"
                placeholder="תאר את המטרה והיעדים של הישיבה..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                מחלקה ראשית <span className="text-red-500">*</span>
              </label>
              <select
                value={meetingData.mainDepartmentId}
                onChange={(e) => setMeetingData(prev => ({ ...prev, mainDepartmentId: e.target.value }))}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white font-medium"
              >
                <option value="">בחר מחלקה</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-blue-600" />
                תאריך ושעת התחלה
              </label>
              <input
                type="datetime-local"
                value={meetingData.startDate}
                onChange={(e) => setMeetingData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
          </div>
        </div>

        {/* Participants */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
              <UserGroupIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">משתתפים</h2>
              <p className="text-sm text-gray-600">בחר את המשתתפים בישיבה</p>
            </div>
          </div>

          {meetingData.mainDepartmentId && (
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                    <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg text-blue-700">
                      עובדים מ{departments.find(d => d.id === meetingData.mainDepartmentId)?.name}
                    </span>
                  </h3>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    {getUsersByDepartment(meetingData.mainDepartmentId).filter(u => meetingData.participantIds.includes(u.id)).length} נבחרו
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getUsersByDepartment(meetingData.mainDepartmentId).map(user => (
                    <label
                      key={user.id}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        meetingData.participantIds.includes(user.id)
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={meetingData.participantIds.includes(user.id)}
                        onChange={() => toggleParticipant(user.id)}
                        className="w-5 h-5 text-blue-600 rounded-md border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                      />
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t-2 border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                    <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg text-purple-700">
                      עובדים ממחלקות אחרות
                    </span>
                  </h3>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                    {getOtherDepartmentUsers().filter(u => meetingData.participantIds.includes(u.id)).length} נבחרו
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getOtherDepartmentUsers().map(user => (
                    <label
                      key={user.id}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        meetingData.participantIds.includes(user.id)
                          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={meetingData.participantIds.includes(user.id)}
                        onChange={() => toggleParticipant(user.id)}
                        className="w-5 h-5 text-purple-600 rounded-md border-2 border-gray-300 focus:ring-2 focus:ring-purple-500 cursor-pointer flex-shrink-0"
                      />
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}

          {!meetingData.mainDepartmentId && (
            <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl">
              <UserGroupIcon className="w-16 h-16 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">
                בחר מחלקה ראשית כדי לראות משתתפים
              </p>
            </div>
          )}
        </div>

        {/* Agenda */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl">
              <DocumentTextIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">אג'נדה / נקודות לדיון</h2>
              <p className="text-sm text-gray-600">הוסף נקודות שיידונו בישיבה</p>
            </div>
          </div>
          
          <div className="flex gap-3 mb-6">
            <input
              type="text"
              value={currentAgendaItem}
              onChange={(e) => setCurrentAgendaItem(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addAgendaItem()}
              className="flex-1 px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              placeholder="למשל: סקירת תוצאות רבעון..."
            />
            <button
              onClick={addAgendaItem}
              className="px-6 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl hover:from-orange-700 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              הוסף
            </button>
          </div>
          
          {meetingData.agendaItems.length === 0 ? (
            <div className="text-center py-8 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl">
              <p className="text-gray-600">לא נוספו נקודות לאג'נדה עדיין</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {meetingData.agendaItems.map((item, index) => (
                <li key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200 group hover:shadow-md transition-all duration-200">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>
                  <span className="flex-1 font-medium text-gray-900">{item}</span>
                  <button
                    onClick={() => removeAgendaItem(index)}
                    className="p-2 hover:bg-red-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <XMarkIcon className="w-5 h-5 text-red-600" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Decisions */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
              <CheckIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">החלטות מנהליות</h2>
              <p className="text-sm text-gray-600">תעד החלטות שהתקבלו בישיבה</p>
            </div>
          </div>
          
          <div className="flex gap-3 mb-6">
            <input
              type="text"
              value={currentDecision}
              onChange={(e) => setCurrentDecision(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addDecision()}
              className="flex-1 px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              placeholder="למשל: אושר תקציב נוסף למחלקת שיווק..."
            />
            <button
              onClick={addDecision}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold flex items-center gap-2"
            >
              <PlusIcon className="w-5 h-5" />
              הוסף
            </button>
          </div>
          
          {meetingData.decisions.length === 0 ? (
            <div className="text-center py-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
              <p className="text-gray-600">לא נוספו החלטות עדיין</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {meetingData.decisions.map((decision, index) => (
                <li key={index} className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 group hover:shadow-md transition-all duration-200">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 text-white flex items-center justify-center">
                    <CheckIcon className="w-5 h-5" />
                  </span>
                  <span className="flex-1 font-medium text-gray-900">{decision}</span>
                  <button
                    onClick={() => removeDecision(index)}
                    className="p-2 hover:bg-red-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <XMarkIcon className="w-5 h-5 text-red-600" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* New Tasks */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">משימות חדשות</h2>
            <button
              onClick={addNewTask}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="w-5 h-5" />
              הוסף משימה
            </button>
          </div>
          
          {meetingData.newTasks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              לא נוצרו משימות חדשות עדיין
            </p>
          ) : (
            <div className="space-y-4">
              {meetingData.newTasks.map((task, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">משימה #{index + 1}</h3>
                    <button
                      onClick={() => removeNewTask(index)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <XMarkIcon className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        value={task.title}
                        onChange={(e) => updateNewTask(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="כותרת המשימה"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <textarea
                        value={task.description}
                        onChange={(e) => updateNewTask(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows="2"
                        placeholder="תיאור המשימה"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <UserGroupIcon className="w-4 h-4 text-blue-600" />
                        אחראים למשימה
                        {task.assigneeIds && task.assigneeIds.length > 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            {task.assigneeIds.length} נבחרו
                          </span>
                        )}
                      </label>
                      <div className="max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 bg-gray-50">
                        <div className="space-y-2">
                          {meetingData.participantIds.map(userId => {
                            const user = users.find(u => u.id === userId);
                            const isSelected = task.assigneeIds && task.assigneeIds.includes(userId);
                            return (
                              <label
                                key={userId}
                                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                                  isSelected
                                    ? 'bg-blue-50 border-blue-300 shadow-md'
                                    : 'bg-white border-gray-200 hover:border-blue-200 hover:bg-blue-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const currentIds = task.assigneeIds || [];
                                    const newIds = e.target.checked
                                      ? [...currentIds, userId]
                                      : currentIds.filter(id => id !== userId);
                                    updateNewTask(index, 'assigneeIds', newIds);
                                  }}
                                  className="w-5 h-5 text-blue-600 rounded-md border-2 border-gray-300 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                />
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold shadow-md">
                                  {(user?.name || user?.email).charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{user?.name}</p>
                                  <p className="text-xs text-gray-500">{user?.email}</p>
                                </div>
                              </label>
                            );
                          })}
                          {meetingData.participantIds.length === 0 && (
                            <div className="text-center py-6 text-gray-500">
                              <UserGroupIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">אין משתתפים בישיבה</p>
                              <p className="text-xs">הוסף משתתפים כדי להקצות משימות</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <input
                        type="date"
                        value={task.deadline}
                        onChange={(e) => updateNewTask(index, 'deadline', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <select
                        value={task.priority}
                        onChange={(e) => updateNewTask(index, 'priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="low">עדיפות נמוכה</option>
                        <option value="medium">עדיפות בינונית</option>
                        <option value="high">עדיפות גבוהה</option>
                        <option value="urgent">דחוף</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New Projects */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">פרויקטים חדשים</h2>
            <button
              onClick={addNewProject}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="w-5 h-5" />
              הוסף פרויקט
            </button>
          </div>
          
          {meetingData.newProjects.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              לא נוצרו פרויקטים חדשים עדיין
            </p>
          ) : (
            <div className="space-y-4">
              {meetingData.newProjects.map((project, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">פרויקט #{index + 1}</h3>
                    <button
                      onClick={() => removeNewProject(index)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <XMarkIcon className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        value={project.name}
                        onChange={(e) => updateNewProject(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="שם הפרויקט"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <textarea
                        value={project.description}
                        onChange={(e) => updateNewProject(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        rows="2"
                        placeholder="תיאור הפרויקט"
                      />
                    </div>
                    
                    <div>
                      <select
                        value={project.managerId}
                        onChange={(e) => updateNewProject(index, 'managerId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">בחר מנהל פרויקט</option>
                        {meetingData.participantIds.map(userId => {
                          const user = users.find(u => u.id === userId);
                          return (
                            <option key={userId} value={userId}>
                              {user?.name || user?.email}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    
                    <div>
                      <input
                        type="date"
                        value={project.deadline}
                        onChange={(e) => updateNewProject(index, 'deadline', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <UserGroupIcon className="w-4 h-4 text-green-600" />
                        חברי צוות
                        {project.teamMembers && project.teamMembers.length > 0 && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                            {project.teamMembers.length} נבחרו
                          </span>
                        )}
                      </label>
                      <div className="max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 bg-gray-50">
                        <div className="space-y-2">
                          {meetingData.participantIds.map(userId => {
                            const user = users.find(u => u.id === userId);
                            const isSelected = project.teamMembers && project.teamMembers.includes(userId);
                            return (
                              <label
                                key={userId}
                                className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                                  isSelected
                                    ? 'bg-green-50 border-green-300 shadow-md'
                                    : 'bg-white border-gray-200 hover:border-green-200 hover:bg-green-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const currentMembers = project.teamMembers || [];
                                    const newMembers = e.target.checked
                                      ? [...currentMembers, userId]
                                      : currentMembers.filter(id => id !== userId);
                                    updateNewProject(index, 'teamMembers', newMembers);
                                  }}
                                  className="w-5 h-5 text-green-600 rounded-md border-2 border-gray-300 focus:ring-2 focus:ring-green-500 cursor-pointer"
                                />
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold shadow-md">
                                  {(user?.name || user?.email).charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{user?.name}</p>
                                  <p className="text-xs text-gray-500">{user?.email}</p>
                                </div>
                              </label>
                            );
                          })}
                          {meetingData.participantIds.length === 0 && (
                            <div className="text-center py-6 text-gray-500">
                              <UserGroupIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                              <p className="text-sm">אין משתתפים בישיבה</p>
                              <p className="text-xs">הוסף משתתפים כדי להקצות פרויקטים</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Linked Tasks */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">קישור למשימות קיימות</h2>
          {tasks.length === 0 ? (
            <p className="text-gray-500 text-center py-4">אין משימות קיימות במערכת</p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-2">
              {tasks.slice(0, 20).map(task => (
                <label
                  key={task.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    meetingData.linkedTaskIds.includes(task.id)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={meetingData.linkedTaskIds.includes(task.id)}
                    onChange={() => toggleLinkedTask(task.id)}
                    className="rounded"
                  />
                  <span className="flex-1">{task.title}</span>
                  <span className="text-xs text-gray-500">{task.status}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Linked Projects */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">קישור לפרויקטים קיימים</h2>
          {projects.length === 0 ? (
            <p className="text-gray-500 text-center py-4">אין פרויקטים קיימים במערכת</p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-2">
              {projects.slice(0, 20).map(project => (
                <label
                  key={project.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    meetingData.linkedProjectIds.includes(project.id)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={meetingData.linkedProjectIds.includes(project.id)}
                    onChange={() => toggleLinkedProject(project.id)}
                    className="rounded"
                  />
                  <span className="flex-1">{project.name}</span>
                  <span className="text-xs text-gray-500">{project.status}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl">
              <DocumentTextIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">סיכום הישיבה</h2>
              <p className="text-sm text-gray-600">סכם את הנקודות המרכזיות והפעולות</p>
            </div>
          </div>
          
          <textarea
            value={meetingData.summary}
            onChange={(e) => setMeetingData(prev => ({ ...prev, summary: e.target.value }))}
            className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 bg-gray-50 hover:bg-white"
            rows="8"
            placeholder="סיכם את הישיבה - נקודות מרכזיות שנידונו, החלטות שהתקבלו, פעולות שיש לבצע, דגשים חשובים..."
          />
          <p className="text-xs text-gray-500 mt-2">טיפ: כתוב סיכום מפורט שיעזור לכל המשתתפים לזכור מה הוחלט</p>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex gap-4 justify-between items-center">
            <button
              onClick={() => navigate('/meetings')}
              className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold"
            >
              ביטול
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold flex items-center gap-2"
              >
                <DocumentTextIcon className="w-5 h-5" />
                {saving ? 'שומר...' : 'שמור טיוטה'}
              </button>
              <button
                onClick={handleCompleteMeeting}
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold flex items-center gap-2"
              >
                <CheckIcon className="w-5 h-5" />
                {saving ? 'מסיים...' : 'סיים ישיבה ושלח'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingEditor;

